import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { EventResponse, Paginated, RegistrationOption } from "@/types";

interface ParentEventDetail extends EventResponse {
  children: EventResponse[];
}

export function useParentEvents(filters: {
  search?: string;
  status?: string;
  cursor?: string;
  limit?: number;
}) {
  return useQuery({
    queryKey: ["parent-events", filters],
    queryFn: () => {
      const params: Record<string, string> = {
        isParent: "true",
        status: filters.status ?? "all",
        limit: String(filters.limit ?? 25),
      };
      if (filters.search) params.search = filters.search;
      if (filters.cursor) params.cursor = filters.cursor;
      return api.get<Paginated<EventResponse>>("/admin/events", params);
    },
  });
}

export function useCreateParentEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      title: string;
      eventDescription: string;
      date: string;
      endDate: string;
      source: string;
      sourceUrl: string;
      status: string;
      isPreview?: boolean;
      registrationOptions?: RegistrationOption[];
      registrationNotes?: string;
    }) => api.post<EventResponse>("/admin/parent-events", data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["parent-events"] });
      void queryClient.invalidateQueries({ queryKey: ["events"] });
    },
  });
}

export function useUpdateParentEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...data
    }: {
      id: string;
      title?: string;
      eventDescription?: string;
      date?: string;
      endDate?: string;
      source?: string;
      sourceUrl?: string;
      isPreview?: boolean;
      registrationOptions?: RegistrationOption[];
      registrationNotes?: string;
    }) => api.put<EventResponse>(`/admin/parent-events/${id}`, data),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["parent-events"] });
      void queryClient.invalidateQueries({ queryKey: ["events"] });
    },
  });
}

export function useSetParentChildren() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ parentId, childEventIds }: { parentId: string; childEventIds: string[] }) =>
      api.post<ParentEventDetail>(`/admin/parent-events/${parentId}/children`, { childEventIds }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["parent-events"] });
      void queryClient.invalidateQueries({ queryKey: ["events"] });
    },
  });
}

export function useRemoveChild() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ parentId, childId }: { parentId: string; childId: string }) =>
      api.delete(`/admin/parent-events/${parentId}/children/${childId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["parent-events"] });
      void queryClient.invalidateQueries({ queryKey: ["events"] });
    },
  });
}
